import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, Trash2, Loader2, Eye, EyeOff, GripVertical, X, Camera } from "lucide-react";

const inputCls = "w-full rounded-lg border border-[var(--njas-border)] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-njas-sage focus:border-njas-sage";
const API_ROOT = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Directors() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // director object or {} for new
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/directors");
      setItems(r.data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (d) => {
    setBusy(d.id);
    try {
      await api.patch(`/directors/${d.id}`, { is_active: !d.is_active });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const del = async (d) => {
    if (!window.confirm(`Delete ${d.name} from the Board?`)) return;
    setBusy(d.id);
    try {
      await api.delete(`/directors/${d.id}`);
      toast.success("Director removed");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const move = async (d, delta) => {
    const idx = items.findIndex((x) => x.id === d.id);
    const swap = items[idx + delta];
    if (!swap) return;
    setBusy(d.id);
    try {
      await Promise.all([
        api.patch(`/directors/${d.id}`, { sort_index: swap.sort_index }),
        api.patch(`/directors/${swap.id}`, { sort_index: d.sort_index }),
      ]);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div className="fade-in max-w-4xl" data-testid="directors-page">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <div className="eyebrow text-njas-sage-hover text-[10px]">Board</div>
          <h2 className="font-serif text-3xl text-njas-navy">Directors</h2>
          <p className="text-sm text-njas-muted mt-1">Manage the Board of Directors record for internal reference and compliance.</p>
        </div>
        <button
          data-testid="add-director-btn"
          onClick={() => setEditing({ name: "", title: "Director", bio: "", email: "", linkedin_url: "", is_active: true, sort_index: items.length })}
          className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-4 py-2 text-sm hover:bg-njas-navy-hover"
        >
          <Plus className="w-4 h-4" /> Add director
        </button>
      </div>

      {loading && <div className="text-sm text-njas-muted">Loading…</div>}

      {!loading && items.length === 0 && (
        <div className="rounded-xl border border-dashed border-[var(--njas-border)] p-8 text-center text-sm text-njas-muted">
          No directors yet. Click <strong>Add director</strong> to create the first entry.
        </div>
      )}

      <div className="space-y-3">
        {items.map((d, i) => (
          <div
            key={d.id}
            data-testid={`director-row-${d.id}`}
            className={`flex items-start gap-3 rounded-xl bg-white border p-4 ${d.is_active ? "border-[var(--njas-border)]" : "border-dashed border-njas-muted/40 bg-njas-bg-alt/40"}`}
          >
            <div className="flex flex-col items-center pt-1">
              <button
                onClick={() => move(d, -1)}
                disabled={i === 0 || busy === d.id}
                className="p-0.5 text-njas-muted hover:text-njas-navy disabled:opacity-30"
                aria-label="Move up"
                data-testid={`director-up-${d.id}`}
              >
                <GripVertical className="w-4 h-4 rotate-180" />
              </button>
              <button
                onClick={() => move(d, +1)}
                disabled={i === items.length - 1 || busy === d.id}
                className="p-0.5 text-njas-muted hover:text-njas-navy disabled:opacity-30"
                aria-label="Move down"
                data-testid={`director-down-${d.id}`}
              >
                <GripVertical className="w-4 h-4" />
              </button>
            </div>

            {d.photo_path ? (
              <img
                src={`${API_ROOT}/public/directors/${d.id}/photo?v=${d.updated_at || ""}`}
                alt={d.name}
                className="w-14 h-14 rounded-full object-cover shrink-0 border border-[var(--njas-border)]"
                data-testid={`director-photo-${d.id}`}
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-njas-sage-tint text-njas-sage-hover flex items-center justify-center font-semibold text-lg shrink-0 border border-[var(--njas-border)]">
                {(d.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
              </div>
            )}

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="font-serif text-lg text-njas-navy">{d.name}</div>
                {!d.is_active && <span className="text-[10px] uppercase tracking-wider text-njas-muted">hidden</span>}
              </div>
              <div className="text-xs text-njas-muted">{d.title}</div>
              {d.bio && <div className="mt-2 text-sm text-njas-text line-clamp-2">{d.bio}</div>}
              <div className="mt-2 flex flex-wrap gap-3 text-xs text-njas-muted">
                {d.email && <span>{d.email}</span>}
                {d.linkedin_url && <span>LinkedIn linked</span>}
              </div>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => toggleActive(d)}
                disabled={busy === d.id}
                data-testid={`director-toggle-${d.id}`}
                title={d.is_active ? "Hide from public site" : "Show on public site"}
                className="p-2 text-njas-muted hover:text-njas-navy hover:bg-njas-bg-alt rounded-lg"
              >
                {busy === d.id ? <Loader2 className="w-4 h-4 animate-spin" /> : d.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
              <button
                onClick={() => setEditing(d)}
                data-testid={`director-edit-${d.id}`}
                className="text-xs px-3 py-1.5 rounded-full border border-[var(--njas-border)] hover:bg-njas-bg-alt text-njas-navy"
              >
                Edit
              </button>
              <button
                onClick={() => del(d)}
                data-testid={`director-delete-${d.id}`}
                disabled={busy === d.id}
                className="p-2 text-njas-muted hover:text-red-600 rounded-lg"
                aria-label="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <DirectorFormModal
          director={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function DirectorFormModal({ director, onClose, onSaved }) {
  const isNew = !director?.id;
  const [name, setName] = useState(director?.name || "");
  const [title, setTitle] = useState(director?.title || "Director");
  const [bio, setBio] = useState(director?.bio || "");
  const [email, setEmail] = useState(director?.email || "");
  const [linkedin, setLinkedin] = useState(director?.linkedin_url || "");
  const [isActive, setIsActive] = useState(director?.is_active ?? true);
  const [busy, setBusy] = useState(false);
  const [photoTs, setPhotoTs] = useState(director?.updated_at || Date.now());
  const [hasPhoto, setHasPhoto] = useState(!!director?.photo_path);
  const [uploading, setUploading] = useState(false);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (isNew) { toast.error("Save the director first, then add a photo"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Photo too large (max 5 MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/directors/${director.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded");
      setPhotoTs(Date.now());
      setHasPhoto(true);
    } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const payload = {
        name, title, bio: bio || null,
        email: email || null,
        linkedin_url: linkedin || null,
        is_active: isActive,
      };
      if (isNew) {
        payload.sort_index = director.sort_index ?? 0;
        await api.post("/directors", payload);
        toast.success("Director added");
      } else {
        await api.patch(`/directors/${director.id}`, payload);
        toast.success("Saved");
      }
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 py-10 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="director-form-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="eyebrow text-njas-sage-hover text-[10px]">{isNew ? "New" : "Edit"}</div>
            <h3 className="font-serif text-2xl text-njas-navy">{isNew ? "Add director" : "Edit director"}</h3>
          </div>
          <button onClick={onClose} className="p-1 text-njas-muted hover:text-njas-navy"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-3">
          {!isNew && (
            <div className="flex items-center gap-4 pb-4 border-b border-[var(--njas-border)]">
              {hasPhoto ? (
                <img
                  src={`${API_ROOT}/public/directors/${director.id}/photo?v=${photoTs}`}
                  alt={director.name}
                  className="w-20 h-20 rounded-full object-cover border border-[var(--njas-border)]"
                  data-testid="director-form-photo-preview"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-njas-sage-tint text-njas-sage-hover flex items-center justify-center font-semibold text-2xl border border-[var(--njas-border)]">
                  {(name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                </div>
              )}
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-wider text-njas-muted mb-1">Profile photo</div>
                <label className="inline-flex items-center gap-2 rounded-full border border-[var(--njas-border)] bg-white text-njas-navy px-3 py-1.5 text-xs hover:bg-njas-bg-alt cursor-pointer" data-testid="director-form-photo-btn">
                  {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                  {hasPhoto ? "Replace photo" : "Upload photo"}
                  <input type="file" accept="image/*" onChange={uploadPhoto} disabled={uploading} className="hidden" data-testid="director-form-photo-input" />
                </label>
                <div className="text-xs text-njas-muted mt-1">Square images work best. Max 5&nbsp;MB.</div>
              </div>
            </div>
          )}
          {isNew && (
            <div className="text-xs text-njas-muted italic pb-2 border-b border-[var(--njas-border)]">
              Save the profile first, then reopen it to upload a photo.
            </div>
          )}
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-njas-muted block mb-1">Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="director-form-name" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-njas-muted block mb-1">Title / role</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} data-testid="director-form-title" placeholder="Director, Chairperson, Treasurer…" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-njas-muted block mb-1">Bio / professional profile</span>
            <textarea rows={6} value={bio} onChange={(e) => setBio(e.target.value)} className={inputCls} data-testid="director-form-bio" placeholder="A short professional bio, credentials and lived-experience relevance…" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-njas-muted block mb-1">Email (optional)</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="director-form-email" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-njas-muted block mb-1">LinkedIn URL (optional)</span>
              <input value={linkedin} onChange={(e) => setLinkedin(e.target.value)} className={inputCls} data-testid="director-form-linkedin" placeholder="https://linkedin.com/in/…" />
            </label>
          </div>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="mt-0.5" data-testid="director-form-active" />
            <span>
              <span className="text-njas-navy font-medium">Active on Board</span>
              <span className="block text-xs text-njas-muted">Uncheck to mark as no longer serving without deleting.</span>
            </span>
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-[var(--njas-border)]">
          <button onClick={onClose} className="rounded-full px-5 py-1.5 text-sm text-njas-muted hover:text-njas-navy">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="director-form-save" className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-5 py-1.5 text-sm hover:bg-njas-navy-hover disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
